import React, { useEffect, useRef, useState } from 'react';
import {
    Avatar,
    TextField,
    Button,
    Alert,
    Box,
    Typography,
    CircularProgress,
    IconButton,
    Stack,
    styled,
    Paper,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import EmailIcon from '@mui/icons-material/Email';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import EditIcon from '@mui/icons-material/Edit';
import { keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';

import authService from '../services/authService';
// Ensure you have an illustration, else it degrades gracefully
import loginIllustration from '../assets/login-illustration.svg';

// CONFIG
const TENANT_ID = '1';
const DEVICE_NAME = 'Admin Web';
const DEVICE_ID = 'admin-web-001';

// Same brand colors as rest of app
const PRIMARY = '#174143';
const ACCENT = '#3d9ea4';
const OTP_LENGTH = 5;
const RESEND_SECONDS = 30;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const RootBox = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: '#f8fafc',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: -200, left: -200, width: 600, height: 600,
        background: `radial-gradient(circle, ${ACCENT}22 0%, transparent 70%)`,
        borderRadius: '50%', zIndex: 0
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -300, right: -100, width: 800, height: 800,
        background: `radial-gradient(circle, ${PRIMARY}15 0%, transparent 70%)`,
        borderRadius: '50%', zIndex: 0
    }
}));

const LoginCard = styled(Paper)(({ theme }) => ({
    width: '100%',
    maxWidth: '1000px',
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    borderRadius: 24,
    border: '1px solid rgba(255,255,255,0.6)',
    zIndex: 1,
    overflow: 'hidden',
    boxShadow: '0 24px 48px -12px rgba(23,65,67,0.15)',
    display: 'flex',
    animation: `${slideUp} 0.6s cubic-bezier(0.16, 1, 0.3, 1)`,
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        maxWidth: '480px',
    },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
    flex: 1,
    background: `linear-gradient(135deg, ${PRIMARY} 0%, #2a6b6f 60%, ${ACCENT} 100%)`,
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
    gap: theme.spacing(4),
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: { padding: theme.spacing(4), minHeight: 180, gap: theme.spacing(2) },
}));

const RightPanel = styled(Box)(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(6),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(4) }
}));

const GradientButton = styled(Button)({
    background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`,
    color: 'white',
    padding: '12px',
    fontSize: '0.95rem',
    fontWeight: 700,
    borderRadius: 12,
    textTransform: 'none',
    boxShadow: '0 4px 14px rgba(61,158,164,0.35)',
    transition: 'all 0.2s',
    '&:hover': {
        background: `linear-gradient(135deg, #0f2d2f, ${PRIMARY})`,
        boxShadow: '0 6px 20px rgba(23,65,67,0.4)',
        transform: 'translateY(-1px)'
    },
    '&:disabled': {
        background: '#e2e8f0',
        color: '#94a3b8',
        boxShadow: 'none'
    }
});

const OtpInput = styled('input')({
    width: 52,
    height: 60,
    textAlign: 'center',
    fontSize: 24,
    fontWeight: 800,
    color: PRIMARY,
    background: '#f8fafc',
    border: '2px solid #e2e8f0',
    borderRadius: 12,
    outline: 'none',
    transition: 'all 0.2s',
    '&:focus': {
        background: '#fff',
        border: `2px solid ${ACCENT}`,
        boxShadow: `0 0 0 4px ${ACCENT}20`,
        transform: 'translateY(-2px)'
    }
});

export default function Login(): React.ReactElement {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [otpVisible, setOtpVisible] = useState(false);
    const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
    const [resendCountdown, setResendCountdown] = useState(0);
    const [loadingRequest, setLoadingRequest] = useState(false);
    const [loadingVerify, setLoadingVerify] = useState(false);
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

    useEffect(() => {
        if (otpVisible) inputsRef.current[0]?.focus();
    }, [otpVisible]);

    useEffect(() => {
        if (resendCountdown === 0) return;
        const id = setInterval(() => setResendCountdown((c) => c - 1), 1000);
        return () => clearInterval(id);
    }, [resendCountdown]);

    const extractMessage = (err: any, fallback = 'Something went wrong') => {
        if (!err) return fallback;
        if (err.response) {
            // server responded with status -> show JSON if present
            try {
                const d = err.response.data;
                if (d && (d.message || d.errors)) return d.message || JSON.stringify(d.errors || d);
                return `Server responded with ${err.response.status}`;
            } catch (e) {
                return fallback;
            }
        }
        return err.message || fallback;
    };

    const handleRequestOtp = async (e?: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        setAlert(null);
        if (!email.includes('@')) {
            setAlert({ type: 'error', message: 'Valid email required' });
            return;
        }
        setLoadingRequest(true);
        try {
            const data = await authService.requestOtp(TENANT_ID, email); // POST /auth/admin/otp with tenantId
            setAlert({ type: 'success', message: (data && (data as any).message) || 'OTP sent!' });
            setOtpVisible(true);
            setOtp(Array(OTP_LENGTH).fill(''));
            setResendCountdown(RESEND_SECONDS);
        } catch (err: any) {
            setAlert({ type: 'error', message: extractMessage(err, 'Failed to send OTP') });
        } finally {
            setLoadingRequest(false);
        }
    };

    const handleResend = async () => {
        setAlert(null);
        if (!email) {
            setAlert({ type: 'error', message: 'Enter email first' });
            return;
        }
        setLoadingRequest(true);
        try {
            const data =
                (authService as any).resendOtp
                    ? await (authService as any).resendOtp(TENANT_ID, email)
                    : await authService.requestOtp(TENANT_ID, email);
            setAlert({ type: 'success', message: (data && (data as any).message) || 'OTP resent' });
            setOtp(Array(OTP_LENGTH).fill(''));
            setResendCountdown(RESEND_SECONDS);
        } catch (err: any) {
            setAlert({ type: 'error', message: extractMessage(err, 'Failed to resend OTP') });
        } finally {
            setLoadingRequest(false);
        }
    };

    const handleVerifyOtp = async () => {
        const code = otp.join('');
        if (code.length !== OTP_LENGTH) return;

        setLoadingVerify(true);
        setAlert(null);
        try {
            const data = await authService.verifyOtp(TENANT_ID, email, code, DEVICE_NAME, DEVICE_ID);
            if (data && (data as any).token) {
                localStorage.setItem('auth_token', (data as any).token);
            }
            setAlert({ type: 'success', message: (data && (data as any).message) || 'Login successful!' });

            const redirectTo = (data && (data as any).redirect) || '/dashboard';
            setTimeout(() => navigate(redirectTo), 600);
        } catch (err: any) {
            setAlert({ type: 'error', message: extractMessage(err, 'Invalid OTP') });
            setOtp(Array(OTP_LENGTH).fill(''));
            inputsRef.current[0]?.focus();
        } finally {
            setLoadingVerify(false);
        }
    }
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const data = e.clipboardData.getData('text');

        // extract only digits and slice to OTP length
        const curOtp = [...otp];
        const pastedArr = data.replace(/\D/g, '').slice(0, OTP_LENGTH).split('');

        if (pastedArr.length === 0) return;

        pastedArr.forEach((char, index) => {
            if (index < OTP_LENGTH) {
                curOtp[index] = char;
            }
        });

        setOtp(curOtp);

        // Focus the appropriate input after pasting
        const nextIndex = Math.min(pastedArr.length, OTP_LENGTH - 1);
        inputsRef.current[nextIndex]?.focus();
    };

    const handleOtpChange = (i: number, val: string) => {
        if (!/^\d?$/.test(val)) return;
        const newOtp = [...otp];
        newOtp[i] = val;
        setOtp(newOtp);
        if (val && i < OTP_LENGTH - 1) inputsRef.current[i + 1]?.focus();
        if (i === OTP_LENGTH - 1 && newOtp.join('').length === OTP_LENGTH) {
            handleVerifyOtp();
        }
    };

    const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otp[i] && i > 0) {
            inputsRef.current[i - 1]?.focus();
        }
    };


    return (
        <RootBox>
            <LoginCard elevation={0}>
                <LeftPanel>
                    {/* Decorative abstract circles */}
                    <Box sx={{ position: "absolute", top: -50, right: -30, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
                    <Box sx={{ position: "absolute", bottom: -20, left: -40, width: 150, height: 150, borderRadius: "50%", background: "rgba(255,255,255,0.08)", pointerEvents: "none" }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, zIndex: 1 }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: 2, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.3)' }}>
                            <SchoolIcon sx={{ fontSize: 26, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', lineHeight: 1.1, letterSpacing: '-0.02em', color: '#fff' }}>
                                Vidya Samruddhi
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', letterSpacing: 0.8, textTransform: 'uppercase', fontWeight: 600 }}>
                                Admin Panel
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        component="img"
                        src={loginIllustration}
                        alt="illustration"
                        onError={(e: any) => e.target.style.display = 'none'}
                        sx={{
                            width: '100%', maxWidth: 320, mt: { xs: 0, md: 4 },
                            animation: `${float} 6s ease-in-out infinite`,
                            filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.2))'
                        }}
                    />

                    <Box sx={{ textAlign: 'center', zIndex: 1 }}>
                        <Typography variant="h5" fontWeight={700} sx={{ mb: 1, letterSpacing: -0.5 }}>
                            Welcome Back
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.85, maxWidth: 280, mx: 'auto', lineHeight: 1.6 }}>
                            Sign in to manage users, monitor doubts, and control your platform.
                        </Typography>
                    </Box>
                </LeftPanel>

                <RightPanel>
                    <Stack spacing={3.5} sx={{ width: '100%', maxWidth: 360 }}>

                        {alert && (
                            <Alert
                                severity={alert.type}
                                onClose={() => setAlert(null)}
                                sx={{ borderRadius: 2, alignItems: 'center' }}
                            >
                                {alert.message}
                            </Alert>
                        )}

                        {!otpVisible ? (
                            <Box component="form" onSubmit={handleRequestOtp}>
                                <Typography variant="h6" fontWeight={800} color={PRIMARY} sx={{ mb: 0.5, letterSpacing: -0.3 }}>
                                    Sign In
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Enter your admin email address to receive an OTP.
                                </Typography>

                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Email Address
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="admin@vidyasamruddhi.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    InputProps={{
                                        startAdornment: <EmailIcon sx={{ color: 'text.disabled', mr: 1, fontSize: 20 }} />,
                                        sx: { borderRadius: 3, background: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: ACCENT } }
                                    }}
                                />

                                <GradientButton fullWidth type="submit" disabled={loadingRequest} sx={{ mt: 3.5 }}>
                                    {loadingRequest ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Send OTP'}
                                </GradientButton>
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="h6" fontWeight={800} color={PRIMARY} sx={{ mb: 0.5, letterSpacing: -0.3 }}>
                                    Verify OTP
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, p: 1.5, background: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <VpnKeyIcon sx={{ color: ACCENT, fontSize: 20 }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">Code sent to</Typography>
                                            <Typography variant="body2" fontWeight={700} color={PRIMARY}>{email}</Typography>
                                        </Box>
                                    </Box>
                                    <IconButton size="small" onClick={() => setOtpVisible(false)} sx={{ color: ACCENT, bgcolor: 'rgba(61,158,164,0.1)' }}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                <Stack direction="row" justifyContent="center" spacing={1.5} sx={{ mb: 4 }}>
                                    {otp.map((d, i) => (
                                        <OtpInput
                                            key={i}
                                            value={d}
                                            onChange={(e) => handleOtpChange(i, e.target.value)}
                                            onKeyDown={(e) => handleKeyDown(i, e)}
                                            onPaste={handlePaste}
                                            ref={(el: HTMLInputElement | null) => { inputsRef.current[i] = el; }}
                                            maxLength={1}
                                            autoComplete="off"
                                        />
                                    ))}
                                </Stack>

                                <GradientButton
                                    fullWidth
                                    onClick={handleVerifyOtp}
                                    disabled={loadingVerify || otp.join('').length !== OTP_LENGTH}
                                >
                                    {loadingVerify ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Verify & Login'}
                                </GradientButton>

                                <Box textAlign="center" mt={3}>
                                    {resendCountdown > 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Resend code in <strong style={{ color: PRIMARY }}>{resendCountdown}s</strong>
                                        </Typography>
                                    ) : (
                                        <Button
                                            variant="text"
                                            onClick={handleResend}
                                            disabled={loadingRequest}
                                            sx={{ color: ACCENT, fontWeight: 700, '&:hover': { background: 'transparent', textDecoration: 'underline' } }}
                                        >
                                            Resend OTP
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </RightPanel>
            </LoginCard>
        </RootBox>
    );
}
